/**
 * The one place that knows the shape of an API path.
 *
 * Two spellings exist for every route and always have: dev proxies the
 * browser's same-origin `/api/*` through a Next rewrite into a worker that
 * mounts under `/api`, while prod and staging serve a dedicated API host with
 * routes at the root. Versioning adds a third variable, so the rule stops
 * being something each call site and each middleware re-derives.
 *
 * It was already going wrong before versioning. `PUBLIC_PATHS` in the auth
 * middleware listed `/api/auth/login` and never matched in production, where
 * the path is `/auth/login`; the audit middleware tested two prefixes to
 * compensate; the rate limiter tested two more; `sectionForAdminPath` chained
 * two `replace` calls. Four hand-rolled copies of one rule, one of them dead.
 */

/** The version segment client-facing routes live under. Bump in one place. */
export const API_VERSION = "v1";

/**
 * Routes that stay outside the version namespace.
 *
 * `/webhook` and `/webhooks` are provider callbacks — their URLs are
 * registered in Stripe, Resend and Zarv ID, and their payload shape is the
 * provider's contract rather than ours. Versioning them would turn every
 * future bump into a coordinated external migration: a new Stripe endpoint
 * means a new signing secret, and verifications already in flight would call
 * back to an address that no longer answers.
 *
 * `/health` is the deploy smoke probe, pinned by `scripts/smoke.sh` and by
 * uptime checks that predate any of this.
 *
 * `/auth/google` holds the Google sign-in redirect and its callback, whose URL
 * is registered on the Google OAuth client — the same case as the webhooks.
 */
const UNVERSIONED = ["/webhook", "/webhooks", "/health", "/auth/google"];

function isUnversioned(path: string): boolean {
  return UNVERSIONED.some((u) => path === u || path.startsWith(`${u}/`));
}

/** Strip one leading segment, but only as a whole segment. */
function stripSegment(path: string, segment: string): string {
  if (path === `/${segment}`) return "/";
  return path.startsWith(`/${segment}/`) ? path.slice(segment.length + 1) : path;
}

/**
 * Turn the path a call site writes into the path the API actually serves.
 *
 * Call sites keep passing the historical `/api/...` spelling — there are
 * hundreds and they carry no information about hosting — and this decides
 * where that lands. The `/api` prefix is dropped, and everything that is not
 * a provider callback gains the version segment.
 *
 * Returns a host-relative path; prefixing the origin (or, in dev, the `/api`
 * the rewrite expects) is the caller's job, because that part genuinely
 * differs between the Next apps and the Vite admin SPA.
 */
export function apiPath(path: string): string {
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  const bare = stripSegment(withSlash, "api");
  if (isUnversioned(bare)) return bare;
  return bare === "/" ? `/${API_VERSION}` : `/${API_VERSION}${bare}`;
}

/**
 * Reduce any spelling of a route to one canonical form for matching.
 *
 * Server-side counterpart to `apiPath`: middleware receives whatever the
 * client sent — `/api/v1/auth/login` in dev, `/v1/auth/login` in prod — and
 * needs a single string to compare against. Both prefixes are optional so a
 * request that predates versioning still normalises, and so the same matcher
 * works in both environments without a second branch.
 */
export function normalizeApiPath(pathname: string): string {
  return stripSegment(stripSegment(pathname, "api"), API_VERSION);
}
